import React, { useDeferredValue, useLayoutEffect, useRef } from 'react';
import * as styles from './TruncatedText.module.scss';

function fitAndTruncateIfNeeded(
  container: HTMLElement,
  value: string,
  options: {
    ellipsis: string;
    keepLast: number;
  },
) {
  const element = container;
  const { keepLast, ellipsis } = options;

  element.textContent = value;
  const containerWidth = Math.round(element.getBoundingClientRect().width);

  if (element.scrollWidth <= containerWidth) return;

  const tail = keepLast > 0 ? value.slice(-keepLast) : '';
  const maxHeadLength = Math.max(0, value.length - keepLast);

  let left = 0;
  let right = maxHeadLength;
  let bestFit = 0;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const head = value.slice(0, mid);

    element.textContent = `${head}${ellipsis}${tail}`;

    if (element.scrollWidth <= containerWidth) {
      bestFit = mid;
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  const finalHead = value.slice(0, bestFit);

  element.textContent = `${finalHead}${ellipsis}${tail}`;
}

const ELLIPSIS = '[…]';

interface TruncatedTextProps {
  children: string;
  keepLast?: number;
}

const TruncatedText = ({ children, keepLast = 0 }: TruncatedTextProps) => {
  const spanRef = useRef<HTMLSpanElement>(null);

  const value = useDeferredValue(children);

  useLayoutEffect(() => {
    const container = spanRef.current;

    if (container) {
      const onResize = () =>
        fitAndTruncateIfNeeded(container, value, {
          keepLast,
          ellipsis: ELLIPSIS,
        });

      const observer = new ResizeObserver(onResize);
      onResize();
      observer.observe(container);

      return () => observer.disconnect();
    }

    return undefined;
  }, [value, keepLast]);

  return <span className={styles.truncatedText} ref={spanRef} />;
};

export default TruncatedText;
